import type { SongReference } from '~/stores/useSongStore'

// Pure helpers for music references — no Firebase, so they're easy to unit test.
//
// `parseReferenceUrl` detects the provider and the stable id from a pasted/shared
// URL. `referenceEmbedUrl` builds the iframe `src` from the parsed id (never from
// raw user input), so the embedded player's host is always one we whitelist in the
// CSP `frame-src` directive — that's what keeps an arbitrary-iframe XSS off the
// table.

export type MusicProvider = SongReference['provider']

export interface ParsedReference {
  provider: MusicProvider
  sourceId: string
  url: string
}

const SPOTIFY_TRACK = /open\.spotify\.com\/(?:[a-z-]+\/)?track\/([a-zA-Z0-9]+)/
const SPOTIFY_URI = /^spotify:track:([a-zA-Z0-9]+)$/
const YOUTUBE_WATCH = /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
const APPLE_MUSIC = /music\.apple\.com\//i

// Try to make sense of a pasted link. Returns null when it isn't a recognised
// Spotify / Apple Music / YouTube reference.
export const parseReferenceUrl = (input: string): ParsedReference | null => {
  const raw = (input || '').trim()
  if (!raw) return null

  const spotify = raw.match(SPOTIFY_TRACK) || raw.match(SPOTIFY_URI)
  if (spotify) {
    const id = spotify[1]
    return { provider: 'spotify', sourceId: id, url: `https://open.spotify.com/track/${id}` }
  }

  const youtube = raw.match(YOUTUBE_WATCH)
  if (youtube) {
    const id = youtube[1]
    return { provider: 'youtube', sourceId: id, url: `https://www.youtube.com/watch?v=${id}` }
  }

  if (APPLE_MUSIC.test(raw)) {
    // Apple's ids are a compound path (storefront/album/.../id?i=songId); keep the
    // canonical url and derive the embed by swapping the host. The path tail is a
    // reasonable stable id for de-duping.
    let sourceId = raw
    try {
      const u = new URL(raw)
      sourceId = u.pathname + u.search
    } catch {
      // keep the raw string as the id if it isn't a parseable URL
    }
    return { provider: 'appleMusic', sourceId, url: raw }
  }

  return null
}

// Build the iframe `src` for the embedded player. Hosts here must stay in sync with
// the CSP `frame-src` allowlist in nuxt.config.ts.
export const referenceEmbedUrl = (ref: Pick<SongReference, 'provider' | 'sourceId' | 'url'>): string => {
  switch (ref.provider) {
    case 'spotify':
      return `https://open.spotify.com/embed/track/${ref.sourceId}`
    case 'youtube':
      return `https://www.youtube.com/embed/${ref.sourceId}`
    case 'appleMusic':
      // music.apple.com/... → embed.music.apple.com/...
      return ref.url.replace('://music.apple.com', '://embed.music.apple.com')
    default:
      return ref.url
  }
}
